import React, { useState, useEffect } from 'react';
import {
  Building2, Search, Plus, Users, CheckCircle, Clock,
  AlertTriangle, Globe, Eye, Settings, X, Loader2, Edit, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const facilityTypes = [
  'Acute Care', 'Long-Term Care', 'Rehabilitation', 'Psychiatric',
  'Ambulatory', 'Home Health', 'Hospice', 'Multi-Site'
];
const statusOptions = ['active', 'pending', 'suspended'];
const tierOptions = ['Basic', 'Professional', 'Enterprise'];

const emptyForm = {
  name: '', type: '', address: '', city: '', state: '', zip_code: '',
  phone: '', email: '', beds: '', admin_name: '', admin_email: '',
  status: 'active', subscription_tier: '', monthly_rate: ''
};

const FacilitiesPage = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadFacilities = () => {
    setLoading(true);
    api.get('/api/facilities')
      .then(setFacilities)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadFacilities(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowModal(true); };
  const openEdit = (facility) => {
    setForm({
      name: facility.name || '', type: facility.type || '',
      address: facility.address || '', city: facility.city || '',
      state: facility.state || '', zip_code: facility.zip_code || '',
      phone: facility.phone || '', email: facility.email || '',
      beds: facility.beds != null ? String(facility.beds) : '',
      admin_name: facility.admin_name || '', admin_email: facility.admin_email || '',
      status: facility.status || 'active',
      subscription_tier: facility.subscription_tier || '',
      monthly_rate: facility.monthly_rate != null ? String(facility.monthly_rate) : '',
    });
    setEditingId(facility.id);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.type) {
      toast.error('Please fill name and type');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        beds: form.beds ? parseInt(form.beds) : null,
        monthly_rate: form.monthly_rate ? parseFloat(form.monthly_rate) : null,
        email: form.email || null,
        admin_email: form.admin_email || null,
      };
      if (editingId) {
        await api.put(`/api/facilities/${editingId}`, payload);
        toast.success('Facility updated successfully');
      } else {
        await api.post('/api/facilities', payload);
        toast.success('Facility created successfully');
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditingId(null);
      loadFacilities();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/facilities/${deleteTarget.id}`);
      toast.success('Facility deleted');
      setDeleteTarget(null);
      loadFacilities();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const filteredFacilities = facilities.filter(f => {
    const matchesSearch = f.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || f.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const activeCount = facilities.filter(f => f.status === 'active').length;
  const pendingCount = facilities.filter(f => f.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="facilities-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Facilities Management</h1>
          <p className="text-gray-500 mt-1">Manage all healthcare organizations using NikoSafe Connect</p>
        </div>
        <Button onClick={openCreate} className="btn-primary gap-2" data-testid="add-facility-btn">
          <Plus className="w-4 h-4" />
          Add New Facility
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Building2 className="w-4 h-4" />
            <span className="text-sm">Total Facilities</span>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="total-facilities-count">{facilities.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Active</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-yellow-600 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Pending</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Users className="w-4 h-4" />
            <span className="text-sm">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{facilities.reduce((a, f) => a + (f.user_count || 0), 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input placeholder="Search facilities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-facilities-input" />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'pending'].map((filter) => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === filter ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Facilities Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredFacilities.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400">No facilities found</div>
          ) : (
            filteredFacilities.map((facility) => (
              <div key={facility.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all group" data-testid="facility-card">
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--niko-teal)]/10 to-[var(--niko-green)]/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-[var(--niko-teal)]" />
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      facility.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {facility.status}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">{facility.name}</h3>
                  <p className="text-sm text-gray-500">{facility.type}</p>
                  {(facility.city || facility.state) && (
                    <div className="flex items-center gap-1 text-sm text-gray-400 mt-1">
                      <Globe className="w-3 h-3" />
                      {[facility.city, facility.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
                <div className="p-6 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{(facility.user_count || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Users</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{facility.beds || '-'}</p>
                    <p className="text-xs text-gray-500">Beds</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--niko-teal)]">${(facility.monthly_rate || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Monthly</p>
                  </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex items-center justify-between">
                  <span className="text-xs text-gray-500">{facility.subscription_tier}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(facility)}
                      className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="Edit Facility"
                      data-testid="edit-facility-btn"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(facility)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete Facility"
                      data-testid="delete-facility-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="facility-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Facility' : 'Add New Facility'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Facility Name *</Label><Input className="mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="facility-name-input" /></div>
              <div>
                <Label>Type *</Label>
                <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="facility-type-select">
                  <option value="">Select type...</option>
                  {facilityTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {editingId && (
                <div>
                  <Label>Status</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="facility-status-select">
                    {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
              )}
              <div><Label>Address</Label><Input className="mt-1" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>City</Label><Input className="mt-1" value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
                <div><Label>State</Label><Input className="mt-1" value={form.state} onChange={e => setForm({...form, state: e.target.value})} /></div>
                <div><Label>ZIP</Label><Input className="mt-1" value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input className="mt-1" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                <div><Label>Beds</Label><Input type="number" className="mt-1" value={form.beds} onChange={e => setForm({...form, beds: e.target.value})} /></div>
              </div>
              <div><Label>Email</Label><Input type="email" className="mt-1" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Admin Name</Label><Input className="mt-1" value={form.admin_name} onChange={e => setForm({...form, admin_name: e.target.value})} /></div>
                <div><Label>Admin Email</Label><Input type="email" className="mt-1" value={form.admin_email} onChange={e => setForm({...form, admin_email: e.target.value})} /></div>
              </div>
              {editingId && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Subscription Tier</Label>
                    <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.subscription_tier} onChange={e => setForm({...form, subscription_tier: e.target.value})} data-testid="facility-tier-select">
                      <option value="">Select tier...</option>
                      {tierOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><Label>Monthly Rate ($)</Label><Input type="number" className="mt-1" value={form.monthly_rate} onChange={e => setForm({...form, monthly_rate: e.target.value})} data-testid="facility-rate-input" /></div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-facility-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingId ? 'Update Facility' : 'Create Facility'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete Facility"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This will remove all associated data and cannot be undone.`}
      />
    </div>
  );
};

export default FacilitiesPage;
